/* Implements Warm Academic theme styling for module page headers */
import React from 'react';
import { LucideIcon, ShieldCheck, Award, Sparkles } from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface ModuleHeaderProps {
  icon: LucideIcon;
  title: string;
  description: string;
  badge?: string;
  color?: 'indigo' | 'medical' | 'emerald' | 'rose' | 'amber' | 'slate';
}

export const ModuleHeader: React.FC<ModuleHeaderProps> = ({ icon: Icon, title, description, badge = "Registry Active", color = 'indigo' }) => {
  const tokens = useThemeTokens();

  if (tokens.isWarmAcademic) {
    return (
      <div className="mb-6 md:mb-10 animate-fade-in no-print">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div className="space-y-3 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="px-3 py-1 rounded-full border bg-[#E6F4F1] text-[#0D6E6E] border-[#0D6E6E]/20 text-[10px] font-bold tracking-wide flex items-center gap-1.5 shadow-xs">
                <ShieldCheck size={12} fill="currentColor" className="opacity-90" />
                {badge}
              </div>
              <div className="px-3 py-1 rounded-full border bg-orange-50 text-orange-800 border-orange-200 text-[10px] font-bold tracking-wide flex items-center gap-1.5 shadow-xs">
                <Award size={12} className="text-[#E05638]" />
                RN & DNP Educator Vetted
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5 pt-1">
              <div className="p-3.5 md:p-4 rounded-2xl shadow-sm bg-[#0D6E6E] text-white shrink-0 border border-[#0B5C5C]">
                <Icon size={26} className="text-white md:w-7 md:h-7" />
              </div>
              <div className="space-y-1">
                <h1 className="text-2xl md:text-3.5xl font-sans font-bold text-[#2D3748] dark:text-slate-100 tracking-tight leading-tight">
                  {title}
                </h1>
                <p className="text-xs md:text-sm text-[#4A5568] dark:text-slate-300 font-medium leading-relaxed max-w-2xl">
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tokens.isClinical) {
    return (
      <div className="mb-6 md:mb-10 animate-fade-in no-print">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div className="space-y-3 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="px-3 py-1 rounded-full border bg-teal-50 text-[#0F7D6C] border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800 text-[10px] font-bold tracking-wide flex items-center gap-1.5 shadow-sm">
                <ShieldCheck size={12} fill="currentColor" className="opacity-90" />
                {badge}
              </div>
              <div className="px-3 py-1 rounded-full border bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 text-[10px] font-bold tracking-wide flex items-center gap-1.5 shadow-sm">
                <Award size={12} className="text-amber-600 dark:text-amber-400" />
                RN & DNP Faculty Reviewed
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5 pt-1">
              <div className="p-3.5 md:p-4 rounded-2xl shadow-md bg-[#0F7D6C] text-white shrink-0 border border-teal-700">
                <Icon size={26} className="text-white md:w-7 md:h-7" />
              </div>
              <div className="space-y-1">
                <h1 className="text-2xl md:text-3.5xl font-serif font-bold text-[#0B2942] dark:text-white tracking-tight leading-tight">
                  {title}
                </h1>
                <p className="text-xs md:text-sm text-slate-600 dark:text-slate-300 font-medium leading-relaxed max-w-2xl">
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tokens.isHighContrast) {
    return (
      <div className="mb-6 md:mb-10 animate-fade-in no-print">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-3">
              <div className="px-3 py-1 rounded-full border-2 border-black bg-amber-300 text-black text-[10px] font-black tracking-wider flex items-center gap-1.5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                <ShieldCheck size={12} fill="currentColor" />
                {badge}
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5">
              <div className="p-3.5 md:p-4 rounded-xl border-2 border-black bg-amber-400 text-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                <Icon size={26} className="text-black md:w-7 md:h-7" />
              </div>
              <div className="space-y-0.5">
                <h1 className="text-2xl md:text-3.5xl font-black text-black dark:text-amber-400 tracking-wide leading-none">
                  {title}
                </h1>
                <p className="text-xs md:text-sm text-black dark:text-slate-200 font-bold leading-relaxed max-w-2xl mt-1">
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tokens.isMinimal) {
    return (
      <div className="mb-6 md:mb-10 animate-fade-in no-print">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-3">
              <div className="px-3 py-1 rounded-md border border-slate-300 bg-slate-100 text-slate-800 text-[10px] font-semibold tracking-wide flex items-center gap-1.5">
                <ShieldCheck size={12} className="text-slate-600" />
                {badge}
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5">
              <div className="p-3.5 md:p-4 rounded-xl bg-slate-800 text-white shrink-0">
                <Icon size={24} className="text-white md:w-6 md:h-6" />
              </div>
              <div className="space-y-0.5">
                <h1 className="text-2xl md:text-3xl font-sans font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
                  {title}
                </h1>
                <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed max-w-2xl">
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tokens.isVibrant) {
    return (
      <div className="mb-6 md:mb-10 animate-fade-in no-print">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-3">
              <div className="px-3 py-1 rounded-full border border-violet-200 bg-violet-100 text-violet-800 text-[10px] font-bold tracking-wide flex items-center gap-1.5 shadow-sm">
                <Sparkles size={12} className="text-violet-600" />
                {badge}
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5">
              <div className="p-3.5 md:p-4 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shrink-0">
                <Icon size={26} className="text-white md:w-7 md:h-7" />
              </div>
              <div className="space-y-0.5">
                <h1 className="text-2xl md:text-3.5xl font-black text-violet-950 dark:text-violet-200 tracking-tight leading-none">
                  {title}
                </h1>
                <p className="text-xs md:text-sm text-slate-600 dark:text-slate-300 font-semibold leading-relaxed max-w-2xl mt-1">
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default Theme Header
  return (
    <div className="mb-6 md:mb-10 animate-fade-in no-print">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-3 flex-1">
          <div className="flex items-center gap-3">
            <div className="px-2.5 py-1 rounded-full border border-indigo-100 bg-indigo-50 text-indigo-700 text-[8px] font-black tracking-widest flex items-center gap-1.5 shadow-sm dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-900">
              <ShieldCheck size={10} fill="currentColor" className="opacity-80" />
              {badge}
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5">
            <div className="p-3 md:p-3.5 rounded-xl md:rounded-2xl shadow-xl bg-indigo-600 border-b-4 border-indigo-900/40 text-white">
              <Icon size={24} className="text-white md:w-7 md:h-7" />
            </div>
            <div className="space-y-0.5">
              <h1 className="text-xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-none">
                {title}
              </h1>
              <p className="text-[10px] md:text-sm text-slate-500 dark:text-slate-400 font-bold tracking-widest opacity-80 leading-relaxed max-w-2xl">
                "{description}"
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

